// queryJuniorSessions

// libraries
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';

import { Button, DatePicker, Form, FormInstance, Input, notification, Select, Table, Tooltip, Typography } from 'antd';
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';

// resources
import * as SnakSammenService from '../../../../services/bb-api/red-cross/snaksammen';
import { txt } from '../../../../locales/i18n';
import { formatDate } from '../../../../utils/date';
import { SharedTable } from '../../../shared/table';
import * as RKStatuses from '../rk-statuses';

const initialFormValues: Record<string, any> = {
	search: '',
	amount: 200,
	date: [null, null],
};

export function SnakSammenSessionsTab() {
	const [sessions, setSessions] = useState<Record<string, any>[]>([]);
	const [matches, setMatches] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(true);

	const formRef = useRef<FormInstance>(null);
	useEffect(() => formRef.current?.submit(), []);

	async function handleSubmit(values: any) {
		setLoading(true);

		try {
			const { records, matches } = await SnakSammenService.querySessions(values);
			setSessions(records);
			setMatches(matches);
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}

		setLoading(false);
	}

	function renderCreated(record: Record<string, any>): JSX.Element {
		return (
			<>
				<Typography.Text>{formatDate(record.sessionDateCreated)}</Typography.Text>
				<br />
				<Typography.Text>{RKStatuses.getSessionStatus(record)}</Typography.Text>
			</>
		);
	}

	function renderSession(record: Record<string, any>): JSX.Element {
		const { sessionDateStart, sessionDateEnd } = record;

		return (
			<>
				<span>{moment(sessionDateStart).format('ddd DD/MM-YY')}</span> <br />
				<span>
					{moment(sessionDateStart).format('HH:mm')} - {moment(sessionDateEnd).format('HH:mm')}
				</span>
				<Typography.Text type="secondary">
					{' '}
					({moment(sessionDateEnd).diff(moment(sessionDateStart), 'minutes')} min.)
				</Typography.Text>
			</>
		);
	}

	function renderVolunteer(record: Record<string, any>): JSX.Element {
		return (
			<>
				<Typography.Text>{record.volunteerUsername}</Typography.Text> <br />
				<Typography.Link href={`mailto:${record.volunteerEmail}`} ellipsis>
					{record.volunteerEmail}
				</Typography.Link>
			</>
		);
	}

	function renderBooker(record: Record<string, any>): JSX.Element | string {
		if (record.bookingStatus === null) return '-';

		return (
			<>
				<Typography.Text>{record.bookingUsername}</Typography.Text> <br />
				<Typography.Link href={`mailto:${record.bookingEmail}`} ellipsis>
					{record.bookingEmail}
				</Typography.Link>
				<br />
				{record.bookingPhone && (
					<Typography.Text>
						+{record.bookingPhonePrefix} {record.bookingPhone}
					</Typography.Text>
				)}
			</>
		);
	}

	function renderBooking(record: Record<string, any>): JSX.Element | string {
		if (record.bookingStatus === null) return '-';

		return (
			<>
				<Typography.Text>{formatDate(record.bookingDateCreated)}</Typography.Text> <br />
				<Typography.Text>{RKStatuses.getBookingStatus(record)}</Typography.Text>
			</>
		);
	}

	function renderAbsence(record: Record<string, any>): JSX.Element | string {
		if (record.absence === null) return '-';

		return (
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				{record.absence.map((absence: Record<string, any>, index: number) => (
					<Typography.Text key={index}>{absence.username}</Typography.Text>
				))}
			</div>
		);
	}

	function renderCanceled(record: Record<string, any>): JSX.Element | string {
		if (record.sessionCancelDate === null && record.bookingCancelDate === null) return '-';

		return (
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				{record.sessionCancelDate && (
					<span>{txt('red-cross.volunteer short') + formatDate(record.sessionCancelDate)}</span>
				)}

				{record.bookingCancelDate && (
					<span>{txt('red-cross.booker short') + formatDate(record.bookingCancelDate)}</span>
				)}
			</div>
		);
	}

	function renderCreatedTitle(): JSX.Element {
		return (
			<>
				<Typography.Text>{txt('global.created')}</Typography.Text>
				<Typography.Text type="secondary"> (Status)</Typography.Text>
			</>
		);
	}

	return (
		<StyledSnakSammenSessionsTab>
			<section style={{ display: 'flex', alignItems: 'center', paddingInline: '2ch' }}>
				<Form layout="inline" initialValues={initialFormValues} onFinish={handleSubmit} ref={formRef}>
					<Tooltip title={txt('global.search')}>
						<Form.Item name="search">
							<Input placeholder={txt('global.search')} allowClear />
						</Form.Item>
					</Tooltip>

					<Tooltip title={txt('global.date')}>
						<Form.Item name="date">
							<DatePicker.RangePicker allowEmpty={[true, true]} />
						</Form.Item>
					</Tooltip>

					<Tooltip title={txt('global.amount')}>
						<Form.Item name="amount">
							<Select dropdownMatchSelectWidth={false}>
								<Select.Option value={50}>50</Select.Option>
								<Select.Option value={200}>200</Select.Option>
								<Select.Option value={500}>500</Select.Option>
								<Select.Option value={1000}>1000</Select.Option>
							</Select>
						</Form.Item>
					</Tooltip>

					<Form.Item>
						<Button type="primary" htmlType="submit">
							{txt('global.search')} <SearchOutlined />
						</Button>
					</Form.Item>

					<Form.Item>
						<Typography.Text type="secondary">
							{loading ? (
								<LoadingOutlined />
							) : (
								txt('global.table query notification', { amount: matches })
							)}
						</Typography.Text>
					</Form.Item>
				</Form>
			</section>

			<SharedTable
				rowKey="sessionId"
				dataSource={sessions}
				loading={loading}
				rowClassName={RKStatuses.getStatusRowClass}
				cursor="default"
			>
				<Table.Column title="Session" render={renderSession} width="24ch" />
				<Table.Column title={renderCreatedTitle} render={renderCreated} width="24ch" />
				<Table.Column title={txt('red-cross.volunteer')} render={renderVolunteer} width="24ch" ellipsis />
				<Table.Column title="Booking" render={renderBooking} width="24ch" />
				<Table.Column title="Booker" render={renderBooker} width="24ch" />
				<Table.Column title={txt('red-cross.absence')} render={renderAbsence} width="18ch" />
				<Table.Column title={txt('red-cross.canceled')} render={renderCanceled} width="24ch" />
			</SharedTable>
		</StyledSnakSammenSessionsTab>
	);
}

const StyledSnakSammenSessionsTab = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1ch;
	height: 100%;
`;
